import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import Tree from 'react-d3-tree';

import { CustomNodeElementProps } from 'react-d3-tree/lib/types/common';

import { useStyles } from './styles';

import { ClientListDialog } from '../ClientListDialog';

import { PartnerHierarchicalViewModel, Partnership } from '../../PartnersHierarchyModels';

interface Child {
  name: string;
  attributes: any;
  children?: Child[];
}

interface ChartData {
  name: string;
  attributes: any;
  children: Child[];
}

interface Props {
  data: PartnerHierarchicalViewModel;
  translation: any;
  selectedCheckboxes: string[];
  isDashboardEnabled: boolean;
}

function isEmail(name: string): boolean {
  return name.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi) !== null;
}

function addDepth(partnerships: Partnership[], depth = 0) {
  partnerships.forEach((partnership) => {
    partnership.depth = depth;

    if (Array.isArray(partnership.subPartnerships)) {
      addDepth(partnership.subPartnerships, depth + 1);
    }
  });
}

export const TreeHierarchy: FC<Props> = ({ data, translation, selectedCheckboxes, isDashboardEnabled }) => {
  const classes = useStyles();

  const treeContainer = useRef<HTMLDivElement>(null);

  const [openDialog, setOpenDialog] = useState(false);

  const [dialogData, setDialogData] = useState<any>(null);

  const [translate, setTranslate] = useState({
    x: 0,
    y: 0,
  });

  const [chartData, setChartData] = useState<ChartData>({
    name: '',
    attributes: {},
    children: [],
  });

  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const updateZoom = () => {
      const width = treeContainer.current?.clientWidth || window.innerWidth;

      if (width <= 500) {
        setZoom(0.2);
      } else if (width <= 700) {
        setZoom(0.3);
      } else if (width <= 900) {
        setZoom(0.4);
      } else if (width <= 1180) {
        setZoom(0.5);
      } else {
        setZoom(1);
      }
    };

    updateZoom();

    window.addEventListener('resize', updateZoom);

    return () => {
      window.removeEventListener('resize', updateZoom);
    };
  }, []);

  const formatPartnerships = useCallback(
    (partnerships: Partnership[]): Child[] => {
      const children: Child[] = [];

      partnerships.forEach((partnership) => {
        const subChildren: Child[] = [];

        if (Array.isArray(partnership.subPartnerships)) {
          subChildren.push(...formatPartnerships(partnership.subPartnerships));
        }

        const child: Child = {
          name: partnership.partnershipCode || '',

          attributes: {
            isRoot: partnership.isRoot,

            type: partnership.partnershipType,

            partnershipEnded: Boolean(partnership.endDate),

            partnershipId: partnership.id,

            scheme: partnership.schemeName,

            startDate: partnership.startDate,

            partnerName: partnership.partnerName,

            partnerEmail: partnership.partnerEmail,

            partnershipCode: partnership.partnershipCode,

            preferredPaymentCurrency: partnership.preferredPaymentCurrency,

            tradingAccountTypes: partnership.tradingAccountTypes,

            clientsNumber: partnership.clientsCount,

            accountsNumber: partnership.accountsCount,

            level: partnership.depth,

            distributionPercentage: partnership.distributionPercentage,
          },

          children: subChildren,
        };

        if (partnership.endDate) {
          if (selectedCheckboxes.includes('terminated')) {
            children.push(child);
          }

          return;
        }

        const isIncluded =
          (selectedCheckboxes.includes('prime') && partnership.partnershipType === 'Prime') ||
          (selectedCheckboxes.includes('master') && partnership.partnershipType === 'Master') ||
          (selectedCheckboxes.includes('ib') && partnership.partnershipType === 'IB' && partnership.isRoot) ||
          (selectedCheckboxes.includes('subIb') && partnership.partnershipType === 'IB' && !partnership.isRoot) ||
          (selectedCheckboxes.includes('mlm') && partnership.partnershipType === 'Mlm');

        if (isIncluded) {
          children.push(child);
        }
      });

      return children;
    },
    [selectedCheckboxes],
  );

  const formatAndSetChartData = useCallback(() => {
    const partnerships = Array.isArray(data.partnerships) ? data.partnerships : [];

    addDepth(partnerships);

    setChartData({
      name: data.name,
      attributes: {
        type: 'partner',
        clientsNumber: data.clientsCount,
        accountsNumber: data.accountsCount,
      },
      children: formatPartnerships(partnerships),
    });
  }, [data, formatPartnerships]);

  const updateTreePosition = useCallback(() => {
    const currentElement = treeContainer.current;

    if (!currentElement) {
      return;
    }

    const size = currentElement.getBoundingClientRect();

    setTranslate({
      x: size.width / 2,
      y: 20,
    });
  }, []);

  useEffect(() => {
    formatAndSetChartData();
    updateTreePosition();
  }, [formatAndSetChartData, updateTreePosition]);

  useEffect(() => {
    window.addEventListener('resize', updateTreePosition);

    return () => {
      window.removeEventListener('resize', updateTreePosition);
    };
  }, [updateTreePosition]);

  const renderCustomElement = ({ nodeDatum, toggleNode }: CustomNodeElementProps): JSX.Element => {
    let type = '';
    let cssClass = '';
    let partnershipEnded = false;
    let attributes: any;
    let rectangleClass = '';
    let rectangleName = '';
    let tradingAccounts: any[] = [];

    if (nodeDatum && nodeDatum.attributes) {
      attributes = nodeDatum.attributes;

      type = String(attributes.type || '');

      partnershipEnded = Boolean(attributes.partnershipEnded);

      tradingAccounts = Array.isArray(attributes.tradingAccountTypes) ? attributes.tradingAccountTypes : [];

      if (type === 'partner' && isEmail(nodeDatum.name)) {
        cssClass = classes.branchNodePartner;
      }

      if (type === 'IB') {
        if (attributes.isRoot) {
          rectangleClass = classes.rectangleIb;

          rectangleName = translation.regular_ib;
        } else {
          rectangleClass = classes.rectangleSubIb;

          rectangleName = translation.sub_ib;
        }
      }

      if (type === 'Master') {
        rectangleClass = classes.rectangleMasterIb;

        rectangleName = translation.master_ib;
      }

      if (type === 'Prime') {
        rectangleClass = classes.rectanglePrime;

        rectangleName = translation.prime_master;
      }

      if (type === 'Mlm') {
        rectangleClass = classes.rectangleMlm;

        rectangleName = attributes.isRoot ? translation.mlm : `${translation.text_328} ${attributes.level}`;
      }

      if (partnershipEnded) {
        rectangleClass = classes.rectangleTerminated;
      }
    }

    const showDetails = () => {
      if (!nodeDatum.attributes) {
        return;
      }

      const details = {
        partnershipId: nodeDatum.attributes.partnershipId,

        partnershipCode: nodeDatum.attributes.partnershipCode,

        clientsNumber: nodeDatum.attributes.clientsNumber,

        accountsNumber: nodeDatum.attributes.accountsNumber,

        email: nodeDatum.attributes.partnerEmail,

        name: nodeDatum.attributes.partnerName,

        type,

        title: rectangleName,

        preferredCurrency: nodeDatum.attributes.preferredPaymentCurrency,

        tradingAccounts,

        isRoot: Boolean(nodeDatum.attributes.isRoot) || Number(nodeDatum.attributes.level) === 0,
      };

      setDialogData(details);
      setOpenDialog(true);
    };

    const partnerContent = (
      <div role="button" tabIndex={0}>
        <div className={`${classes.textDiv} ${classes.partner}`}>{nodeDatum.name}</div>

        <div className={classes.clientsContent}>
          <div
            className={classes.partnerClients}
            style={{
              marginRight: 3,
            }}
          >
            <div className={classes.title}>{translation.text_6387}</div>

            <div className={classes.partnerClientsValue}>{nodeDatum.attributes?.clientsNumber}</div>
          </div>

          <div className={classes.partnerClients}>
            <div className={classes.title}>{translation.text_6388}</div>

            <div className={classes.partnerClientsValue}>{nodeDatum.attributes?.accountsNumber}</div>
          </div>
        </div>
      </div>
    );

    const partnershipContent = (
      <div
        className={classes.partnershipContent}
        onClick={showDetails}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            showDetails();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className={classes.partnershipCode}>{nodeDatum.name.toUpperCase()}</div>

        <div className={classes.partnerName}>{attributes?.partnerName}</div>

        {attributes?.scheme && (
          <div>
            <b>{translation.text_6390}: </b>

            {attributes.scheme}
          </div>
        )}

        {attributes?.distributionPercentage !== undefined && attributes?.distributionPercentage !== null && (
          <div>
            <b>{translation.rebate_distribution_percentage || 'Rebate Distribution %'}: </b>
            {attributes.distributionPercentage}%
          </div>
        )}

        <div className={classes.clientsContent}>
          <div
            className={classes.clients}
            style={{
              marginRight: 3,
            }}
          >
            <div className={classes.title}>{translation.text_6387}</div>

            <div className={classes.value}>{attributes?.clientsNumber}</div>
          </div>

          <div className={classes.clients}>
            <div className={classes.title}>{translation.text_6388}</div>

            <div className={classes.value}>{attributes?.accountsNumber}</div>
          </div>
        </div>
      </div>
    );

    const content = (
      <div className={`${classes.branchNode} ${cssClass}`} role="button" tabIndex={0}>
        {type !== 'partner' && attributes && <div className={`${classes.rectangle} ${partnershipEnded ? classes.rectangleTerminated : rectangleClass}`}>{rectangleName}</div>}

        <div>{type === 'partner' ? partnerContent : partnershipContent}</div>
      </div>
    );

    const isPartnerEmailNode = type === 'partner' && isEmail(nodeDatum.name);

    return (
      <svg x={isPartnerEmailNode ? '-150px' : '-100px'} y="-20">
        <foreignObject className={classes.foreignObject} width="200" height={isPartnerEmailNode ? '80' : '150'} onClick={toggleNode}>
          <div>{content}</div>
        </foreignObject>
      </svg>
    );
  };

  return (
    <>
      <div className={classes.chartRoot} ref={treeContainer}>
        <Tree
          data={chartData}
          translate={translate}
          collapsible={false}
          transitionDuration={0}
          separation={{
            siblings: 1,
            nonSiblings: 1,
          }}
          orientation="vertical"
          nodeSize={{
            x: 350,
            y: 360,
          }}
          renderCustomNodeElement={renderCustomElement}
          pathFunc="step"
          zoom={zoom}
          pathClassFunc={() => classes.line}
        />
      </div>

      <ClientListDialog key={dialogData?.partnershipCode} translation={translation} data={dialogData} open={openDialog} handleCancel={() => setOpenDialog(false)} isDashboardEnabled={isDashboardEnabled} />
    </>
  );
};
